import styled from '@emotion/styled';
import {
  Icon,
  Tooltip,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import type {
  InterventionReportWeeks,
  StudentInterventionWeeks,
} from '@innovamat/glow-api-client';
import { ArithmeticFluencyTendency } from '@innovamat/glow-api-client';
import { dates } from '@innovamat/radiance-utils';
import type { TFunction } from 'i18next';
import {
  getFixedLocalizedNumber,
  isNullOrUndefined,
} from '../../../utils/common';
import { FlexContainerCentered, StyledP } from '../../../utils/render-rows';
import { getReportsTypography } from '../../../utils/typography';

export const EllipsableText = styled(Typography.Body2)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompletedContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const TrendIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 3px;
  align-self: flex-end;
  margin-bottom: 2px;
  width: 16px;
  min-width: 16px;
  justify-content: center;
`;

const BarContainer = styled.div`
  display: flex;
  width: 45px;
  height: 4px;
  background-color: white;
  border-radius: 4px;
  overflow: hidden;
`;

const BarFragment = styled.div<{ color: string }>`
  flex-grow: 1;
  background-color: ${({ color }) => color};
  margin: 0 1px;

  &:first-child {
    margin-left: 0;
    border-top-left-radius: 25px;
    border-bottom-left-radius: 25px;
  }
  &:last-child {
    margin-right: 0;
    border-top-right-radius: 25px;
    border-bottom-right-radius: 25px;
  }
`;

const WeekSublabel = styled(Typography.Body3)<{
  isBoldSublabel: boolean;
}>`
  width: 45px;
  height: 16px;
  pointer-events: auto;
  font-weight: ${({ isBoldSublabel }) => (isBoldSublabel ? 'bold' : 'normal')};
  color: ${({ isBoldSublabel, theme }) =>
    isBoldSublabel
      ? theme.tokens.color.alias.cm.text.text.value
      : theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const MeanSessionsBadge = styled.div<{
  backgroundColor: string;
  textColor: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  font-weight: 700;
  color: ${({ textColor }) => textColor};
  height: 32px;
  min-width: 64px;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const StyledPNoData = styled.p`
  ${getReportsTypography()}
  color: ${({ theme }) =>
    theme.tokens.color.specific.reports.semantic['no-data'].value};
`;

const renderEllipsableText = (
  value: string,
  dataTestId: string
): JSX.Element => {
  return (
    <EllipsableText data-testid={dataTestId}>
      <p>{value}</p>
    </EllipsableText>
  );
};

type ArithmeticFluencyCellProps = {
  current: number | null;
  dataTestId: string;
  arithmeticFluencyTendency?: ArithmeticFluencyTendency | null;
  someStudentHasTendency?: boolean;
};

const TREND_ICON_CONFIG = {
  [ArithmeticFluencyTendency.Up]: {
    icon: 'TrendUpIcon' as const,
    colorKey: 'super-high' as const,
  },
  [ArithmeticFluencyTendency.Down]: {
    icon: 'TrendDownIcon' as const,
    colorKey: 'low' as const,
  },
  [ArithmeticFluencyTendency.Equal]: {
    icon: 'TrendEqualIcon' as const,
    colorKey: 'medium' as const,
  },
} as const;

const ArithmeticFluencyCell = ({
  current,
  dataTestId,
  arithmeticFluencyTendency,
  someStudentHasTendency = false,
}: ArithmeticFluencyCellProps): JSX.Element => {
  const theme = useGlimmerTheme();

  const renderTrendIcon = (): JSX.Element | null => {
    if (!arithmeticFluencyTendency) {
      return null;
    }

    const config = TREND_ICON_CONFIG[arithmeticFluencyTendency];
    if (!config) {
      return null;
    }

    const color =
      arithmeticFluencyTendency === ArithmeticFluencyTendency.Equal
        ? theme.tokens.color.alias.cm.icon['icon-default'].value
        : theme.tokens.color.specific.reports.semantic[config.colorKey].value;

    return <Icon icon={config.icon} size="M" iconColor={color} />;
  };

  if (isNullOrUndefined(current)) {
    return (
      <FlexContainerCentered data-testid={dataTestId}>
        <StyledPNoData>-</StyledPNoData>
      </FlexContainerCentered>
    );
  }

  return (
    <FlexContainerCentered data-testid={dataTestId}>
      <StyledP>{getFixedLocalizedNumber(current!)}</StyledP>
      {someStudentHasTendency && (
        <TrendIconContainer>{renderTrendIcon()}</TrendIconContainer>
      )}
    </FlexContainerCentered>
  );
};

const renderArithmeticFluencyCell = (
  current: number | null,
  dataTestId: string,
  arithmeticFluencyTendency?: ArithmeticFluencyTendency | null,
  someStudentHasTendency?: boolean
): JSX.Element => {
  return (
    <ArithmeticFluencyCell
      current={current}
      dataTestId={dataTestId}
      arithmeticFluencyTendency={arithmeticFluencyTendency}
      someStudentHasTendency={someStudentHasTendency}
    />
  );
};

const getMeanSessionsColor = (
  value: number | null,
  theme: ReturnType<typeof useGlimmerTheme>
): string => {
  const baseTokenReport = theme.tokens.color.specific.reports;

  if (isNullOrUndefined(value)) {
    return baseTokenReport['progress-bar'].empty.value;
  }

  const numValue = parseFloat(value!.toFixed(2));

  if (numValue < 1) {
    return baseTokenReport.semantic.low.value;
  }
  if (numValue < 2.2) {
    return baseTokenReport.semantic.medium.value;
  }
  if (numValue < 3) {
    return baseTokenReport.semantic.high.value;
  }
  return baseTokenReport.semantic['super-high'].value;
};

const getMeanSessionsTextColor = (
  value: number | null,
  theme: ReturnType<typeof useGlimmerTheme>
): string => {
  if (isNullOrUndefined(value)) {
    return theme.tokens.color.specific.reports.semantic['no-data'].value;
  }
  return theme.tokens.color.alias.cm.surface['surface-primary'].value;
};

const MeanSessionsRenderer = ({
  value,
  dataTestId,
}: {
  value: number | null;
  dataTestId: string;
}): JSX.Element => {
  const theme = useGlimmerTheme();
  const bgColor = getMeanSessionsColor(value, theme);
  const textColor = getMeanSessionsTextColor(value, theme);
  return (
    <FlexContainerCentered data-testid={dataTestId}>
      <MeanSessionsBadge backgroundColor={bgColor} textColor={textColor}>
        <Typography.Subtitle2>
          {isNullOrUndefined(value) ? '-' : getFixedLocalizedNumber(value!, 2)}
        </Typography.Subtitle2>
      </MeanSessionsBadge>
    </FlexContainerCentered>
  );
};

const renderMeanSessions = (
  value: number | null,
  dataTestId: string
): JSX.Element => {
  return <MeanSessionsRenderer value={value} dataTestId={dataTestId} />;
};

const renderCompletedSessionsSublabels = (
  weeks: InterventionReportWeeks,
  t: TFunction<'translation', undefined, 'translation'>,
  currentWeekNumber: number
): JSX.Element => {
  return (
    <CompletedContainer>
      {weeks.interventionWeeks.map((week, index) => {
        const isBoldSublabel = index + 1 === currentWeekNumber;
        return (
          <Tooltip
            content={
              <div>{`
              ${dates.getFormattedDayMonthDate(
                week.startDate
              )} - ${dates.getFormattedDayMonthDate(week.endDate)}`}</div>
            }
            popperOptions={{ strategy: 'fixed' }}
          >
            <WeekSublabel key={index} isBoldSublabel={isBoldSublabel}>
              {t('reports.catchup-up.subtitle.numberOfTheWeek', {
                numOfTheWeek: index + 1,
              })}
            </WeekSublabel>
          </Tooltip>
        );
      })}
    </CompletedContainer>
  );
};

const CompletedSessions = ({
  completed,
  total,
  notYet,
  id,
  lastWeekInIntervention,
  studentId,
  numberOfTheWeek,
}: {
  completed: number;
  total: number;
  notYet: boolean;
  id: string;
  lastWeekInIntervention: number;
  studentId: number;
  numberOfTheWeek: number;
}): JSX.Element => {
  const showWarning = completed === 0;
  const theme = useGlimmerTheme();

  const getColor = (index: number): string => {
    const { semantic } = theme.tokens.color.specific.reports;
    if (!completed || index > completed) {
      return semantic['no-data'].value;
    }
    const percentage = (completed / total) * 100;
    switch (true) {
      case percentage <= 25:
        return semantic.low.value;
      case percentage <= 50:
        return semantic.medium.value;
      case percentage <= 75:
        return semantic.high.value;
      case percentage <= 100:
        return semantic['super-high'].value;
      default:
        return semantic['no-data'].value;
    }
  };

  const isNotLongerInIntervention = !!lastWeekInIntervention as boolean;
  const prefixDataTestId = isNotLongerInIntervention
    ? 'INTER_DEATABLE'
    : 'INTER_TABLE';

  return (
    <ColumnContainer id={id}>
      <FlexContainerCentered
        isError={showWarning}
        isDisabled={notYet}
        data-testid={`${prefixDataTestId}_WEEKLYFRACT_W${numberOfTheWeek}_S${studentId}`}
      >
        <StyledP>{`${notYet ? '-' : completed}/`}</StyledP>
        <Typography.Subtitle2>{total}</Typography.Subtitle2>
      </FlexContainerCentered>
      <FlexContainerCentered>
        <BarContainer
          data-testid={`${prefixDataTestId}_BARS_W${numberOfTheWeek}_S${studentId}`}
        >
          <BarFragment color={getColor(1)} />
          <BarFragment color={getColor(2)} />
          <BarFragment color={getColor(3)} />
          <BarFragment color={getColor(4)} />
        </BarContainer>
      </FlexContainerCentered>
    </ColumnContainer>
  );
};

const renderCompletedSession = (
  weeks: StudentInterventionWeeks[],
  currentWeek: number,
  lastWeekInIntervention: number,
  studentId: number
): JSX.Element => {
  return (
    <CompletedContainer id="completed-sessions-column">
      {weeks.map((week: StudentInterventionWeeks, index) => {
        const { completedInterventionSessions, availableInterventionSessions } =
          week;
        const isCurrentWeek = index + 1 === currentWeek;
        const weekId = isCurrentWeek
          ? `current-week-${index + 1}`
          : `week-${index + 1}`;
        if (lastWeekInIntervention && index + 1 > lastWeekInIntervention)
          return <></>;
        return (
          <CompletedSessions
            completed={completedInterventionSessions}
            total={availableInterventionSessions}
            notYet={index + 1 > currentWeek}
            id={`${weekId}`}
            lastWeekInIntervention={lastWeekInIntervention}
            studentId={studentId}
            numberOfTheWeek={index + 1}
          />
        );
      })}
    </CompletedContainer>
  );
};

export {
  renderArithmeticFluencyCell,
  renderCompletedSession,
  renderCompletedSessionsSublabels,
  renderEllipsableText,
  renderMeanSessions,
};
